import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  systemSettingsApi,
  type EnrollmentSourceItem,
  type SystemSettings,
  type UpdateSystemSettings,
} from "../../api/system-settings.api";
import { ApiError } from "../../api/transport";
import { useHasPermission } from "../../auth/HasPermission";

const { Title, Text } = Typography;

/**
 * ADM-032 系统设置页（business-config:SC-12~SC-25 前端）。
 *
 * 五个卡片：交易与报名 / 教务自动化 / 提醒与通知 / 公众号通知 / 报名来源。
 * 每项变更 PATCH /system-settings（partial），成功提示「已保存」，错误展示 message。
 * 数值/文本项失焦且有变化才提交（避免逐字符提交中间值），开关点击即提交。
 * 公众号总开关闭时三个子 Switch disabled（SC-18 前端级联）。
 */
export function SettingsPage() {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [newSourceLabel, setNewSourceLabel] = useState("");
  const [loading, setLoading] = useState(false);
  // ADM-32 EDIT 控制：无编辑权限时只读（校区管理员种子角色仅 VIEW），越权 PATCH 由后端 403 兜底
  const canEdit = useHasPermission("ADM-32", "EDIT");

  const fetchSettings = useCallback(async () => {
    try {
      const data = await systemSettingsApi.getSettings();
      setSettings(data);
    } catch (e) {
      message.error((e as ApiError).message ?? "加载系统设置失败");
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  const save = useCallback(async (patch: UpdateSystemSettings) => {
    if (!canEdit) {
      message.warning("当前账号无系统设置编辑权限");
      return;
    }
    setLoading(true);
    try {
      const updated = await systemSettingsApi.updateSettings(patch);
      setSettings(updated);
      message.success("已保存");
    } catch (e) {
      message.error((e as ApiError).message ?? "保存失败");
    } finally {
      setLoading(false);
    }
  }, [canEdit]);

  if (!settings) {
    return <div>加载中…</div>;
  }

  const masterDisabled = !settings.wechatNotifyEnabled;

  return (
    <div>
      {!canEdit && (
        <Alert
          type="info"
          showIcon
          message="当前账号无系统设置编辑权限，仅可查看"
          style={{ marginBottom: 12 }}
        />
      )}
      <div className="page-header-row">
        <div>
          <Title level={3}>系统设置</Title>
          <p className="page-header-desc">
            业务配置参数（对应 sys_parameter），修改即时生效并写入操作日志审计。
          </p>
        </div>
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="交易与报名" size="small" style={{ marginBottom: 16 }}>
            <NumberItem label="收款链接有效时长" desc="收款入口生成后默认的有效时长" value={settings.paymentLinkExpireMinutes} min={5} max={1440} unit="分钟" onChange={(v) => save({ paymentLinkExpireMinutes: v })} loading={loading} />
            <NumberItem label="报名申请有效天数" desc="超过有效期的报名申请不再允许转订单" value={settings.enrollmentValidDays} min={1} max={365} unit="天" onChange={(v) => save({ enrollmentValidDays: v })} loading={loading} />
            <NumberItem label="订单支付有效期" desc="待支付订单超过该时长未支付将进入过期流程" value={settings.orderExpireHours} min={1} max={720} unit="小时" onChange={(v) => save({ orderExpireHours: v })} loading={loading} />
            <SwitchItem label="超时未支付自动取消" desc="定时任务扫描超时订单并自动置为已取消" checked={settings.orderAutoCancelEnabled} onChange={(v) => save({ orderAutoCancelEnabled: v })} loading={loading} />
          </Card>

          <Card title="教务自动化" size="small" style={{ marginBottom: 16 }}>
            <SwitchItem label="试课用尽自动移出班级" desc="试课余额归 0 且未缴费时，自动结束在班关系" checked={settings.trialExhaustionAutoExitEnabled} onChange={(v) => save({ trialExhaustionAutoExitEnabled: v })} loading={loading} />
          </Card>
        </Col>

        <Col span={12}>
          <Card title="提醒与通知" size="small" style={{ marginBottom: 16 }}>
            <SwitchItem label="尾款到期自动提醒" desc="尾款任务自动向家长/顾问发送提醒并标记逾期" checked={settings.balanceReminderEnabled} onChange={(v) => save({ balanceReminderEnabled: v })} loading={loading} />
            <NumberItem label="尾款提前提醒天数" desc="到期前 N 天开始发送提醒" value={settings.balanceReminderAheadDays} min={0} max={30} unit="天" onChange={(v) => save({ balanceReminderAheadDays: v })} loading={loading} />
          </Card>

          <Card title="公众号通知" size="small" style={{ marginBottom: 16 }}>
            <SwitchItem label="公众号通知总开关" desc="关闭时以下三类通知均不发送" checked={settings.wechatNotifyEnabled} onChange={(v) => save({ wechatNotifyEnabled: v })} loading={loading} />
            <SwitchItem label="缴费成功通知" checked={settings.wechatNotifyPaymentSuccess} disabled={masterDisabled} onChange={(v) => save({ wechatNotifyPaymentSuccess: v })} loading={loading} />
            <SwitchItem label="尾款到期通知" checked={settings.wechatNotifyBalanceDue} disabled={masterDisabled} onChange={(v) => save({ wechatNotifyBalanceDue: v })} loading={loading} />
            <SwitchItem label="排课变更通知" checked={settings.wechatNotifyScheduleChange} disabled={masterDisabled} onChange={(v) => save({ wechatNotifyScheduleChange: v })} loading={loading} />
          </Card>

          <Card title="报名来源" size="small" style={{ marginBottom: 16 }}>
            <Table
              scroll={{ x: 'max-content' }}
              size="small"
              rowKey="value"
              pagination={false}
              dataSource={settings.enrollmentSources}
              columns={[
                { title: "来源标识", dataIndex: "value" },
                {
                  title: "显示名",
                  dataIndex: "label",
                  render: (_: string, r: EnrollmentSourceItem) => (
                    <SourceLabelInput
                      record={r}
                      sources={settings.enrollmentSources}
                      loading={loading}
                      onCommit={(next) => save({ enrollmentSources: next })}
                    />
                  ),
                },
                {
                  title: "操作",
                  fixed: 'right',
                  width: 70,
                  render: (_: unknown, r: EnrollmentSourceItem) => (
                    <Popconfirm
                      title="确认删除该来源？"
                      okText="删除"
                      okButtonProps={{ danger: true }}
                      onConfirm={() =>
                        save({
                          enrollmentSources: settings.enrollmentSources.filter(
                            (x) => x.value !== r.value,
                          ),
                        })
                      }
                    >
                      <Button type="text" danger>
                    删除
                  </Button> 
                    </Popconfirm>
                  ),
                },
              ]}
            />
            <Space style={{ marginTop: 12 }}>
              <Input
                size="small"
                placeholder="新来源显示名，如：地推活动"
                value={newSourceLabel}
                onChange={(e) => setNewSourceLabel(e.target.value)}
                style={{ width: 200 }}
              />
              <Button
                size="small"
                icon={<PlusOutlined />}
                disabled={!newSourceLabel.trim()}
                onClick={() => {
                  const label = newSourceLabel.trim();
                  save({
                    enrollmentSources: [
                      ...settings.enrollmentSources,
                      { value: `CUSTOM_${Date.now()}`, label },
                    ],
                  });
                  setNewSourceLabel("");
                }}
              >
                添加
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

/** 布尔开关行。 */
function SwitchItem({
  label,
  desc,
  checked,
  disabled,
  onChange,
  loading,
}: {
  label: string;
  desc?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  loading: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f5f5f5" }}>
      <div>
        <div>{label}</div>
        {desc && <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>}
      </div>
      <Switch checked={checked} disabled={disabled || loading} onChange={onChange} />
    </div>
  );
}

/** 数值输入行。键入只更新草稿，失焦且有变化才提交，避免逐字符 PATCH 中间值。 */
function NumberItem({
  label,
  desc,
  value,
  min,
  max,
  unit,
  onChange,
  loading,
}: {
  label: string;
  desc?: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  onChange: (v: number) => void;
  loading: boolean;
}) {
  const [draft, setDraft] = useState<number | null>(value);

  // 保存成功回填（或外部值变化）时同步草稿
  useEffect(() => setDraft(value), [value]);

  /** 失焦提交：自算 clamp（不依赖 rc-inputnumber 纠边界的内部时序）；清空/未变则回退显示原值。 */
  const commitOnBlur = () => {
    const next = draft === null ? null : Math.min(max, Math.max(min, draft));
    if (next !== null && next !== value) {
      onChange(next);
    } else {
      setDraft(value);
    }
  };

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f5f5f5" }}>
      <div>
        <div>{label}</div>
        {desc && <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>}
      </div>
      <InputNumber min={min} max={max} value={draft} addonAfter={unit} disabled={loading} onChange={(v) => setDraft(v)} onBlur={commitOnBlur} />
    </div>
  );
}

/** 报名来源显示名行内编辑。键入只更新草稿，失焦且有变化才提交整表 enrollmentSources。 */
function SourceLabelInput({
  record,
  sources,
  onCommit,
  loading,
}: {
  record: EnrollmentSourceItem;
  sources: EnrollmentSourceItem[];
  onCommit: (next: EnrollmentSourceItem[]) => void;
  loading: boolean;
}) {
  const [draft, setDraft] = useState(record.label);

  // 保存成功回填（或外部值变化）时同步草稿
  useEffect(() => setDraft(record.label), [record.label]);

  /** 失焦提交：trim 后有变化才提交；空白/未变则回退显示原值。 */
  const commitOnBlur = () => {
    const next = draft.trim();
    if (next && next !== record.label) {
      onCommit(sources.map((x) => (x.value === record.value ? { ...x, label: next } : x)));
    } else {
      setDraft(record.label);
    }
  };

  return (
    <Input
      size="small"
      value={draft}
      style={{ width: 140 }}
      disabled={loading}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commitOnBlur}
    />
  );
}
